/**
 * Utilities for manipulating/validating strings
 *
 * @module lib/StringUtils
 */
import HtmlReactParser from 'html-react-parser';
import flatten from 'lodash/flatten';
import has from 'lodash/has';
import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Link } from 'react-router';

import * as Constants from '../constants';
import { localeCurrencyMap } from '../constants/Strings';
import Logger from '../tools/Logger';

import { decode } from './HtmlUtils';

const log = Logger.child({ component: 'lib/StringUtils' });

export function decodeHtml(html) {
  return decode(html);
}

/**
 * @param {Integer} price - original price integer returned from Api
 * @returns {string} a string with the correct formatting (e.g. 4.99 instead of 499)
 */
export function convertPriceToString(price) {
  if (!price) {
    return '0';
  }
  const rawStringPrice = price.toString();
  const stringPrice = rawStringPrice.padStart(2, '0');
  const convertedPrice =
    stringPrice.substring(0, stringPrice.length - 2) +
    '.' +
    stringPrice.substring(stringPrice.length - 2);
  return convertedPrice;
}

export function formatPrice(price, locale) {
  const value = convertPriceToString(price);
  const priceProps = {
    value,
    style: 'currency',
    currency: localeCurrencyMap[locale],
  };
  return <FormattedNumber {...priceProps} />;
}

export function returnPriceString(price, locale) {
  if (locale !== 'en-US') {
    return (
      <FormattedMessage
        {...Constants.Strings.messages.nonUSPrices}
        values={{ price: formatPrice(price, locale) }}
      />
    );
  }
  return formatPrice(price, locale);
}

export function getLastFourDigits(cardNumber) {
  if (cardNumber) {
    const stringCardNumber = cardNumber.toString();
    const lastFourDigits = stringCardNumber.substring(stringCardNumber.length - 4);
    return lastFourDigits;
  }
}

export function constructAmazonMp3Url(token) {
  if (!token) {
    return null;
  }
  const base = 'http://www.amazon.com/dp/';
  const affiliateSuffix = '/?tag=wwwpandoracom-20';
  return `${base}${token}${affiliateSuffix}`;
}

/**
 * check if a string is numeric
 *
 * @param {string} str - string to be tested for numeric
 * @returns {boolean} true or false depending on regex evaluation
 */
export function isNumericString(str) {
  if (/^[\d*]+$/.test(str)) {
    return true;
  }
  return false;
}

/**
 * Adds parameters to url as string queries, e.g. http://url.com?key=value
 *
 * @param {string} url - the url to modify
 * @param {object} params - key/value pairs
 * @returns {string} the modified url
 */
export function addUrlParameters(url, params) {
  let newUrl = url + '?';
  for (const key in params) {
    if (has(params, key)) {
      newUrl += `${key}=${params[key]}`;
    }
  }
  return newUrl;
}

export function b64DecodeUnicode(str) {
  return decodeURIComponent(
    Array.prototype.map
      .call(atob(str), function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );
}

/**
 * If called with string where % is not encoded, returns string as is without throwing an error
 * Specifically used mapStateToProps for search where route parses as searchTerm and vice versa
 *
 * @param {string} uri - pathname to parse
 * @returns {string} the decoded url
 */
export function decodeURIComponentSafe(uri) {
  let out = '';
  let arr = [];
  let x = '';
  if (uri) {
    arr = uri.split(/(%(?:d0|d1)%.{2})/);
  }
  for (let i = 0; i < arr.length; i++) {
    try {
      x = decodeURIComponent(arr[i]);
    } catch (e) {
      x = arr[i];
    }
    out += x;
  }
  return out;
}

/**
 * Disables the ability of the user to copy text
 */
export function disableCopy() {
  if (document.selection) {
    document.selection.empty();
  } else {
    window.getSelection().removeAllRanges();
  }
}

/**
 * Convert html string into a react component
 *
 * @param {string} str - string with html
 * @param {Function} reactComponentReplacer - swap node with custom react components. https://www.npmjs.com/package/html-react-parser#replacedomnode
 * @returns {object} A react component
 */
export function parseStringToReactComponent(str, reactComponentReplacer) {
  // Workaround to prevent ReactHtmlParser from prematurely adding a closing </p> after a chunk of text
  const htmlStr = str.replace('<p>', '<br /><br />').replace('</p>', '');

  const parserConfig = reactComponentReplacer
    ? {
        replace: reactComponentReplacer,
      }
    : null;

  const parsed = HtmlReactParser(htmlStr, parserConfig);
  return (typeof parsed === 'string' && [parsed]) || parsed;
}

// The replace method for html-react-parser. See https://www.npmjs.com/package/html-react-parser for usage.
// This is a fallback method in the very rare case that the catalog api returns with unintentionally decoded angle brackets.
// More details in these jiras https://jira.savagebeast.com/browse/CONTENG-6214 / https://jira.savagebeast.com/browse/WEBCLIENT-7548
const validNodeNames = [
  'a',
  'abbr',
  'b',
  'br',
  'cite',
  'code',
  'data',
  'em',
  'q',
  's',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'time',
  'p',
];
const validNodeTypes = 'tag';

export function descriptionBackstageReactComponentReplacer(domNode) {
  const { name, type } = domNode;
  // for catalog descriptions, we only use a and span tags.
  // If the created node has an invalid tag name, throw an error.
  // An example would be if the backend fails to translate '<ArtistEntityId:42566|Desmond Child>'
  // to '<a data-pandora-id="AR:735721" data-shareable-url="/artist/desmond-child-and-rouge/ARf496qJkV7vtrc"
  // href="pandorav4://backstage/artist?pandoraId=AR:735721">Desmond Child</a>'
  // the domNode.name would equal 'artistentityid:42566|desmond', which is invalid
  if (type === validNodeTypes && !validNodeNames.includes(name)) {
    throw new Error(`Backstage description: tried to render invalid tagname of ${name}`);
  }
}

let descriptionBackstageAnchorStringIndex = 5000;
export function getDescriptionBackstageAnchorStringIndex() {
  return ++descriptionBackstageAnchorStringIndex;
}

/**
 *
 * @param {string} str - Content of the component
 * @param {string|number} key - Unique identifier for the component
 * @param {Function} getBackstageLocation -  A function that generates the backstage url from pandoraId and path
 * @param {Function} onLinkClick - Handler for the click event of the Link component
 * @returns {Link}
 */
export function parseDescriptionBackstageAnchorStringToLinkComponent(
  str,
  key,
  getBackstageLocation,
  onLinkClick,
) {
  let linkUrl;
  let linkText;
  const shareableUrlMatch = str.match(/data-shareable-url="(.*?)"/);
  const pandoraIdMatch = str.match(/data-pandora-id="(.*?)"/);
  const linkTextMatch = str.match(/">(.*?)<\/a/);

  if (shareableUrlMatch) {
    linkUrl = shareableUrlMatch[1];
  }

  if (linkTextMatch) {
    linkText = linkTextMatch[1];
  }

  if (!linkUrl && !linkText) {
    return null;
  }

  if (!linkUrl) {
    return React.createElement(
      'span',
      { key: getDescriptionBackstageAnchorStringIndex() },
      decodeHtml(linkText),
    );
  }

  if (!linkText) {
    linkText = `${window.location.origin}${linkUrl}`;
  }

  const location = getBackstageLocation({
    pandoraId: pandoraIdMatch && pandoraIdMatch[1],
    path: linkUrl,
  });

  // If <span> tag exists within <a> tag, convert it to a react component
  const isSpan = linkText.includes('<span ');
  const linkComponent = isSpan
    ? parseStringToReactComponent(linkText, descriptionBackstageReactComponentReplacer)
    : decodeHtml(linkText);
  return (
    <Link to={location} key={key} onClick={onLinkClick}>
      {linkComponent}
    </Link>
  );
}

/**
 *
 * @param {string} html - A string containing html
 * @param {Function} getBackstageLocation -  A function that generates the backstage url from pandoraId and path
 * @param {Function} onLinkClick - Handler for the click event of the Link component
 * @returns {Array}
 */
export function parseDescriptionBackstageStringToReactComponent(
  html,
  getBackstageLocation,
  onLinkClick,
) {
  try {
    // Split str into either a chunk of text or an <a> tag link
    const textArray = html.split(/(<a(\s)+.*?<\/a>)/);
    return textArray.map((text, index) => {
      const isLink = text.startsWith('<a ');
      return isLink
        ? parseDescriptionBackstageAnchorStringToLinkComponent(
            text,
            index,
            getBackstageLocation,
            onLinkClick,
          )
        : parseStringToReactComponent(text, descriptionBackstageReactComponentReplacer);
    });
  } catch (error) {
    log.error(error.message);
    return React.createElement(
      'span',
      { key: getDescriptionBackstageAnchorStringIndex() },
      'Description not found',
    );
  }
}

export function alphabetizeComparator(a, b) {
  if (a && b) {
    return a.toString().localeCompare(b.toString());
  }
}

/**
 * Convert hex string and alpha to string of rgba value for use with CSS style
 *
 * @param {string} hex - must be 6-character web color string (no hash prefix)
 * @param {int} alpha - number 0 to 100 for opacity value
 * @returns {string} rgba value
 */
export function hexStringToRgba(hex, alpha = 100) {
  if (!hex || (hex && (hex.length !== 6 || Number.isNaN(parseInt(hex, 16))))) {
    return 'rgba(0, 0, 0, ' + alpha + ')';
  }
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Parse the copyright and phonorecord Information
 *
 * @param {string} str - a string with copyright or phonorecord
 * @returns {Array}- the array
 */
export function parseCopyrightInfo(str) {
  let replacedStr = str.replace(/\(C\)/g, '©').replace(/\(P\)/g, '℗');
  if (!replacedStr.includes('©') && !replacedStr.includes('℗')) {
    replacedStr = '© ' + replacedStr;
  }
  const splitStr = replacedStr.split(/(?=[©℗])|[↵]/);
  const tempArray = [];
  for (const currItem of splitStr) {
    if (currItem.includes('©')) {
      tempArray.unshift(currItem.trim());
    } else {
      tempArray.push(currItem.trim());
    }
  }
  return tempArray;
}

export function truncateReactComponentArray(reactArr, maxStrLength) {
  let flatArr = flatten(reactArr);
  let foundLastText = false;

  // Iterate and keep text/links upto maxStrLength
  // eslint-disable-next-line complexity
  flatArr =
    flatArr &&
    flatArr.reduce((arr, val) => {
      // If ignore <br> for truncated text
      if (!isString(val) && !val.props.children) {
        if (val.type !== 'br') {
          arr.push(val);
        }
        return arr;
      }

      const valLength = isString(val) ? val.length : val.props.children.length;
      const currentLength = arr.reduce((sum, el) => {
        if (!isArray(el) && !el.props.children) {
          return sum;
        }
        return isArray(el) ? sum + el[0].length : sum + el.props.children.length;
      }, 0);

      if (currentLength + valLength < maxStrLength && !foundLastText) {
        // Append chunk since it's short enough
        if (isString(val) && val.trim().length > 0) {
          arr.push([val]);
        } else if (!isString(val)) {
          arr.push(val);
        }
        return arr;
      } else if (!foundLastText) {
        // Last chunk needs to be shortened
        let charsLeft = maxStrLength - currentLength;
        let str = '';
        if (isString(val)) {
          str = val;
        } else {
          str = val.props.children;
          // Keep descending through children until we reach a string
          while (!isString(str) && str.props && str.props.children) {
            str = str.props.children;
          }
        }

        // Keep enough words to stay below limit
        const splits = isString(str) ? str.split(' ') : [];
        const trailingWords = [];
        splits.forEach(word => {
          if (word.length <= charsLeft) {
            trailingWords.push(word);
            charsLeft -= word.length + 1;
          } else {
            charsLeft = -1;
          }
        });

        let finalVal;
        if (isString(val)) {
          finalVal = [trailingWords.join(' ')];
        } else {
          finalVal = React.cloneElement(val, val.props, [trailingWords.join(' ')]);
        }

        arr.push(finalVal);
      }
      foundLastText = true;
      return arr;
    }, []);

  return flatArr;
}

export function trim(str) {
  return str.replace(/^\s+|\s+$/gm, '');
}

export function randomString(length) {
  const max = Math.pow(10, length);
  const min = Math.pow(10, length - 1);
  return Math.floor(Math.random() * (max - min) + min).toString();
}
